import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { ErrorMessage, getIn, useFormikContext } from "formik";
import classNames from "classnames";

import { states } from "@default/paragraphs/webform/states";
import { getValueFromStringObjPath } from "@default/paragraphs/webform/webform-elements";
import DashedField from "./dashed-field";

const SelectFieldReact = ({
  item,
  compositeIndex,
  compositeParent,
  defaultMenuIsOpen,
}) => {
  const { values, setFieldValue, handleBlur, errors, touched } =
    useFormikContext();

  const { invisible, visible, enabled, disabled, optional, required } = states(
    item["#states"],
    values
  );

  const value = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? getValueFromStringObjPath(values, compositeParent)[compositeIndex][
          item.id
        ]
      : getValueFromStringObjPath(values, compositeParent)[item.id]
    : values[item.id];

  const identifier = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? `${compositeParent}[${compositeIndex}][${item.id}]`
      : `${compositeParent}[${item.id}]`
    : item.id;
  const fieldError = getIn(errors, identifier);
  const fieldTouched = getIn(touched, identifier);

  const options = item["#options"]
    ? Object.keys(item["#options"]).map((key) => ({
        value: key,
        label: item["#options"][key],
      }))
    : [];

  const handleChange = (selectedOption) => {
    const selectedValue = item["#multiple"]
      ? selectedOption.map((option) => option.value)
      : selectedOption?.value || "";
    setFieldValue(identifier, selectedValue);
  };

  return (
    <div
      className={classNames("form-group cc-select", {
        hidden: invisible || !visible,
      })}
      style={item["#flex"] ? { flex: item["#flex"] } : {}}
    >
      <label htmlFor={identifier} className="form-label">
        {item["#title"]}
        {(!!item["#required"] || required) && !optional && visible && (
          <span className="required">*</span>
        )}
      </label>

      <DashedField>
        <Select
          inputId={identifier}
          instanceId={identifier}
          name={identifier}
          options={options}
          value={
            item["#multiple"]
              ? options.filter((option) => value?.includes(option.value))
              : options.find((option) => option.value === value) || null
          }
          onChange={handleChange}
          onBlur={handleBlur}
          isMulti={!!item["#multiple"]}
          isDisabled={!enabled || disabled || item["#disabled"]}
          placeholder={item["#empty_option"] || "- Please select -"}
          classNamePrefix="cc-select"
          defaultMenuIsOpen={defaultMenuIsOpen}
          hideSelectedOptions={false}
          isClearable={true}
          className={classNames({
            "form-control": true,
            "is-invalid": fieldError && fieldTouched,
          })}
        />
      </DashedField>

      <ErrorMessage
        role="region"
        aria-live="polite"
        component="span"
        name={identifier}
        className="error-message"
      />

      {!!item["#description"] && (
        <small
          className="form-description text-muted form-text"
          dangerouslySetInnerHTML={{ __html: item["#description"] }}
        />
      )}
    </div>
  );
};

SelectFieldReact.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    "#states": PropTypes.object,
    "#flex": PropTypes.number,
    "#title": PropTypes.string,
    "#required": PropTypes.bool,
    "#description": PropTypes.string,
    "#multiple": PropTypes.bool,
    "#options": PropTypes.objectOf(PropTypes.string),
    "#empty_option": PropTypes.string,
    "#empty_value": PropTypes.string,
    "#disabled": PropTypes.bool,
  }),
  compositeIndex: PropTypes.number,
  compositeParent: PropTypes.string,
  defaultMenuIsOpen: PropTypes.bool,
};

export default SelectFieldReact;
